import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './lib/i18n';
import './styles/index.css';
import { initMonitoring } from './lib/monitoring';
import { routeTree } from './routeTree.gen';

// Before the first render, so an error during startup is still reported.
initMonitoring();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * web-team-rules.md "In-browser mock API": only ever loaded when
 * `VITE_MOCK_API === 'true'` (a dedicated e2e build, or `VITE_MOCK_API=true
 * pnpm dev`), via a dynamic import so a production build tree-shakes the
 * whole mock layer away. Installed before the first render so the app's very
 * first `fetch` (the session query) is already mocked.
 */
async function installMocksIfEnabled(): Promise<void> {
  if (import.meta.env['VITE_MOCK_API'] !== 'true') return;
  const { installMockApi } = await import('./mocks/install');
  installMockApi();
}

async function bootstrap(): Promise<void> {
  await installMocksIfEnabled();

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root container #root is missing from index.html');
  }

  createRoot(container).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

void bootstrap();
